import { computed, inject, type ComputedRef, type InjectionKey } from "vue";

/**
 * Fixed width (px) of the desktop chat column in the new channel layout shell
 * (channel-layout-v2.vue). Mirrors the legacy sticky chat sidebar's 320px width
 * so the page region the rails bleed into is computed as
 * `viewport - CHANNEL_CHAT_COLUMN_WIDTH` when chat is open on large desktop.
 */
export const CHANNEL_CHAT_COLUMN_WIDTH = 320;

/**
 * Injection key for the new channel layout's page-region width signal.
 *
 * `channel-layout-v2.vue` provides a reactive width (px) describing the
 * horizontal viewport region the centered page column occupies:
 *  - chat closed / mobile  -> the full viewport width.
 *  - chat open on desktop  -> the viewport minus the chat column width.
 *
 * The full-bleed QUESTS / REWARDS rails (`full-bleed-rail.vue`) consume this
 * so the rail breakout is computed from the page region's own width, NOT from
 * `100vw`, and therefore never bleeds under the chat column in the two-column
 * state.
 *
 * Descendants may also read the equivalent CSS variable
 * `--channel-page-region-width` set on the page-column wrapper.
 */
export const channelPageRegionWidthKey: InjectionKey<ComputedRef<number>> =
  Symbol("channelPageRegionWidth");

/**
 * Consumer-side helper for the page-region width contract. Returns a reactive
 * width ref; falls back to `null` when used outside the v2 shell (e.g. under the
 * legacy layout) so callers can detect absence and degrade gracefully.
 */
export function useChannelPageRegionWidth(): ComputedRef<number | null> {
  const provided = inject(channelPageRegionWidthKey, null);
  return computed(() => provided?.value ?? null);
}
